import React from 'react';
import { Link } from 'react-router-dom';
import { AspectRatio } from './ui/aspect-ratio';
import { Badge } from './ui/badge';
import { motion } from 'framer-motion';
import { Clock, Eye } from 'lucide-react';

export const ArticleCard = ({ article }) => {
  const [isVertical, setIsVertical] = React.useState(false);
  
  if (!article) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    const isPortrait = img.naturalHeight > img.naturalWidth;
    setIsVertical(isPortrait);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.38, ease: 'easeOut' }}
    >
      <Link 
        to={`/articles/${article.slug}`}
        data-testid="article-card" 
        className="group block h-full"
      >
        <div className="relative h-full overflow-hidden rounded-lg border border-border bg-[hsl(var(--surface-2))] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.45)] hover:-translate-y-1">
          {/* Image with Smart Aspect Ratio */}
          <AspectRatio ratio={isVertical ? 3/4 : 16/10}>
            <div className="overflow-hidden h-full">
              <img
                src={article.heroImageUrl}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                onLoad={handleImageLoad}
              />
            </div>
          </AspectRatio>

          {/* Content */}
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {article.category && (
                <Badge 
                  data-testid="article-card-tag"
                  className="px-2.5 py-1 rounded-full text-xs uppercase tracking-wide bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.3)] hover:bg-[hsl(var(--primary)/0.18)]"
                >
                  {article.category}
                </Badge>
              )}
              {article.readingTime && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {article.readingTime} min
                </span>
              )}
              {article.views !== undefined && article.views > 0 && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {article.views.toLocaleString()}
                </span>
              )}
            </div>

            <h3 
              data-testid="article-card-title"
              className="font-display text-xl md:text-2xl font-semibold leading-tight mb-2 group-hover:text-primary transition-colors duration-200"
            >
              {article.title}
            </h3>

            {article.dek && (
              <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                {article.dek}
              </p>
            )}

            {article.publishedAt && (
              <p className="text-xs text-muted-foreground mt-3">
                {formatDate(article.publishedAt)}
              </p>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};